import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// Emoticon to emoji mappings
export const emoticonMap = {
  ":)": "😊",
  ":-)": "😊",
  ":(": "😢",
  ":-(": "😢",
  ":D": "😃",
  ":-D": "😃",
  ";)": "😉",
  ";-)": "😉",
  ":P": "😛",
  ":-P": "😛",
  ":p": "😛",
  ":-p": "😛",
  "<3": "❤️",
  ":o": "😮",
  ":-o": "😮",
  ":O": "😮",
  ":-O": "😮",
  xD: "😆",
  XD: "😆",
  ":/": "😕",
  ":-/": "😕",
  "B)": "😎",
  "B-)": "😎",
  ":*": "😘",
  ":-*": "😘",
  "^_^": "😊",
  o_o: "😳",
  O_O: "😳",
  ">:(": "😠",
  ">:-(": "😠",
  ":'(": "😢",
  ":')": "😂",
  ":|": "😐",
  ":-|": "😐",
};

// Create regex pattern that matches all emoticons
// Sort by length descending to match longer patterns first (e.g., ":-)" before ":)")
const emoticonPatterns = Object.keys(emoticonMap)
  .sort((a, b) => b.length - a.length)
  .map((emoticon) => emoticon.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  .join("|");

const emoticonRegex = new RegExp(`(${emoticonPatterns})(?=\\s|$)`, "g");

// Sort emoticons by length (longest first) to check longer patterns first
const sortedEmoticons = Object.entries(emoticonMap).sort(
  ([a], [b]) => b.length - a.length,
);

// For plain text fields (e.g. chat): emoticons that stand on their own
// (start of text or after a space) become emojis, so ":/" in a link or ":o"
// in a word is left alone
const isWholeWord = (text, start, end) =>
  (start === 0 || /\s/.test(text[start - 1])) &&
  (end === text.length || /\s/.test(text[end]));

// Replace all standalone emoticons in a text (used when it is sent)
export const replaceEmoticons = (text) =>
  text.replace(/\S+/g, (word, offset) =>
    emoticonMap[word] && isWholeWord(text, offset, offset + word.length)
      ? emoticonMap[word]
      : word,
  );

// While typing: if a space was just typed after an emoticon, replace it.
// Returns the new text and cursor position, or null if nothing changed.
export const replaceTypedEmoticon = (text, cursor) => {
  if (cursor < 2 || !/\s/.test(text[cursor - 1])) return null;
  const before = text.slice(0, cursor - 1);
  for (const [emoticon, emoji] of sortedEmoticons) {
    const start = before.length - emoticon.length;
    if (before.endsWith(emoticon) && isWholeWord(text, start, before.length)) {
      return {
        text: text.slice(0, start) + emoji + text.slice(before.length),
        cursor: cursor - emoticon.length + emoji.length,
      };
    }
  }
  return null;
};

export const EmoticonReplacer = Extension.create({
  name: "emoticonReplacer",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("emoticonReplacer"),

        props: {
          handleTextInput(view, from, to, text) {
            const { state, dispatch } = view;

            // Don't trigger on Enter/newline
            if (text === "\n") return false;

            // Get text before cursor including what was just typed
            const $pos = state.doc.resolve(from);
            const textBefore =
              $pos.parent.textContent.slice(0, $pos.parentOffset) + text;

            // Check each emoticon (longest first) to see if text ends with it
            for (const [emoticon, emoji] of sortedEmoticons) {
              if (textBefore.endsWith(emoticon)) {
                // Found a match - replace it
                const startPos = from - emoticon.length + text.length;

                // Replace the emoticon with emoji and add a space
                const tr = state.tr;
                tr.delete(startPos, to);
                tr.insertText(emoji + " ", startPos);
                dispatch(tr);

                return true;
              }
            }

            return false;
          },
        },
      }),
    ];
  },
});
